import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  useBulkRemoveLocationUsers,
  useLocationsList,
  useUsersInLocation,
} from '../api';
import type { UserInLocationRow } from '../types';

export function BulkDeleteLocationPage() {
  const locations = useLocationsList();
  const [locationId, setLocationId] = useState<string>('');
  const members = useUsersInLocation(locationId || null);
  const remove = useBulkRemoveLocationUsers();

  const [doSwap, setDoSwap] = useState(false);
  const [swapLocationId, setSwapLocationId] = useState<string>('');
  const [pending, setPending] = useState<{ ids: string[]; clear: () => void } | null>(null);

  const columns = useMemo<ColumnDef<UserInLocationRow>[]>(
    () => [
      {
        accessorKey: 'userId',
        header: 'User ID',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.userId}
          </span>
        ),
      },
      { accessorKey: 'username', header: 'Username' },
      {
        accessorKey: 'locationName',
        header: 'Location',
        cell: ({ row }) => row.original.locationName ?? '—',
      },
    ],
    [],
  );

  const locationLabel =
    (locations.data ?? []).find((l) => String(l.locationId) === locationId)?.locationName ??
    '';
  const swapLabel =
    (locations.data ?? []).find((l) => String(l.locationId) === swapLocationId)
      ?.locationName ?? '';

  const performRemove = () => {
    if (!pending) return;
    if (doSwap && !swapLocationId) {
      toast.error('Pick a location to swap to, or untick the swap option.');
      return;
    }
    remove.mutate(
      {
        locationId,
        userIds: pending.ids.join(','),
        swapLocationId: doSwap ? swapLocationId : undefined,
      },
      {
        onSuccess: (res) => {
          toast.success(
            res?.message ??
              (doSwap
                ? `Swapped ${pending.ids.length} users to ${swapLabel}`
                : `Removed ${pending.ids.length} users from ${locationLabel}`),
          );
          pending.clear();
          setPending(null);
        },
        onError: (err) => toast.error(readErrorMessage(err) ?? 'Bulk remove failed.'),
      },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="Bulk Remove Users from Location"
        description="Detach many users from a location at once, optionally moving them to another location."
        actions={
          <Button asChild variant="outline">
            <Link to="/location-list">
              <ArrowLeft className="h-4 w-4" />
              Back to locations
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Source location</CardTitle>
          <CardDescription>
            Showing users currently attached to this location.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-md">
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    locations.isLoading ? 'Loading locations…' : 'Select a location'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(locations.data ?? []).map((l) => (
                  <SelectItem key={l.locationId} value={String(l.locationId)}>
                    {l.locationName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2">
              <Checkbox
                id="do-swap"
                checked={doSwap}
                onCheckedChange={(v) => {
                  setDoSwap(Boolean(v));
                  if (!v) setSwapLocationId('');
                }}
              />
              <Label htmlFor="do-swap" className="cursor-pointer">
                Swap users into another location instead of removing
              </Label>
            </div>
            {doSwap && (
              <div className="max-w-md flex-1">
                <Select value={swapLocationId} onValueChange={setSwapLocationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a target location to swap into" />
                  </SelectTrigger>
                  <SelectContent>
                    {(locations.data ?? [])
                      .filter((l) => String(l.locationId) !== locationId)
                      .map((l) => (
                        <SelectItem key={l.locationId} value={String(l.locationId)}>
                          {l.locationName}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <DataTable<UserInLocationRow, unknown>
        tableId="bulk:remove-location:v1"
        columns={columns}
        data={members.data ?? []}
        isLoading={members.isFetching}
        searchPlaceholder="Search members…"
        emptyTitle={locationId ? 'No users in this location' : 'Pick a location to begin'}
        emptyDescription={
          locationId
            ? 'This location has no users to remove.'
            : 'Choose a source location from the dropdown above.'
        }
        getRowId={(row) => String(row.userId)}
        enableRowSelection
        exportFilename="location-members.csv"
        pageSize={25}
        renderSelectionActions={(ids, clear) => (
          <Button
            size="sm"
            variant={doSwap ? 'default' : 'destructive'}
            onClick={() => {
              if (doSwap && !swapLocationId) {
                toast.error('Pick a location to swap to.');
                return;
              }
              setPending({ ids, clear });
            }}
            disabled={ids.length === 0 || remove.isPending}
          >
            <Trash2 className="h-4 w-4" />
            {doSwap
              ? `Swap ${ids.length} to ${swapLabel || 'location'}`
              : `Remove ${ids.length}`}
          </Button>
        )}
      />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title="Are you sure you want to delete?"
        description={
          doSwap ? (
            <>
              Move <span className="font-medium text-foreground">{pending?.ids.length ?? 0}</span>{' '}
              user{pending?.ids.length === 1 ? '' : 's'} from{' '}
              <span className="font-medium text-foreground">{locationLabel}</span> to{' '}
              <span className="font-medium text-foreground">{swapLabel}</span>?
            </>
          ) : (
            <>
              Remove <span className="font-medium text-foreground">{pending?.ids.length ?? 0}</span>{' '}
              user{pending?.ids.length === 1 ? '' : 's'} from{' '}
              <span className="font-medium text-foreground">{locationLabel}</span>?
            </>
          )
        }
        confirmLabel={doSwap ? 'Swap users' : 'Remove'}
        tone={doSwap ? 'default' : 'destructive'}
        loading={remove.isPending}
        onConfirm={performRemove}
      />
    </FillPage>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
